import { computed, type ComputedRef } from 'vue';

import { useTheme } from 'vuetify';

import { useAppTheme } from '@/composables/useAppTheme';
import {
  clearIntercomUser,
  DEFAULT_INTERCOM_COLOR,
  initIntercom as bootIntercom,
  updateIntercomUserData
} from '@/utils/intercomUtils';

type UseIntercomReturn = {
  intercomColor: ComputedRef<{ action: string; background: string }>;
  intercomThemeMode: ComputedRef<'dark' | 'light'>;
  updateIntercomTheme: () => void;
  resetIntercom: () => void;
  clearIntercomUser: () => void;
};

/**
 * Composable for the Vue-facing parts of Intercom: Vuetify-theme-aware
 * colors, theme updates, and the login-page reset.
 *
 * The SDK itself boots app-wide in main.js (intercomUtils.initIntercom) so
 * window.Intercom exists before useAuth's initApp() syncs the user identity
 * on session restore. User identity syncing lives in useAuth.ts, not here.
 */
export const useIntercom = (): UseIntercomReturn => {
  const vTheme = useTheme();
  const { themeName } = useAppTheme();

  // Theme-aware colors
  const intercomColor = computed(() => ({
    action:
      (vTheme.current.value.dark
        ? vTheme.current.value.colors['primary-700']
        : vTheme.current.value.colors.primary) || DEFAULT_INTERCOM_COLOR,
    background: vTheme.current.value.colors.primary || DEFAULT_INTERCOM_COLOR
  }));

  const intercomThemeMode = computed(() => themeName.value as 'dark' | 'light');

  /**
   * Apply the current Vuetify theme colors to Intercom.
   */
  const updateIntercomTheme = () => {
    updateIntercomUserData({
      action_color: intercomColor.value.action,
      background_color: intercomColor.value.background,
      theme_mode: intercomThemeMode.value
    });
  };

  /**
   * Reset Intercom (shutdown and reboot) when mounting the login page, so a
   * previous user's Intercom session doesn't linger for the next person on
   * this browser. Reboots with brand defaults, then re-applies the live theme.
   */
  const resetIntercom = () => {
    clearIntercomUser();
    bootIntercom();
    updateIntercomTheme();
  };

  return {
    intercomColor,
    intercomThemeMode,
    updateIntercomTheme,
    resetIntercom,
    clearIntercomUser
  };
};
